/**
 * DeliveryRegistrations.jsx — Phase 2B
 * Admin view of all registrations across offerings.
 * Supports filtering by offering, status, and withdraw action.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Users, RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { AssessmentDeliveryShell } from "../components/assessment-delivery/AssessmentDeliveryShell";
import { DeliveryStatusBadge } from "../components/assessment-delivery/DeliveryStatusBadge";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function authH() {
  return { Authorization: `Bearer ${localStorage.getItem("academiaos_token")}` };
}

async function apiFetch(path, opts = {}) {
  const r = await fetch(`${API}${path}`, {
    headers: { ...authH(), "Content-Type": "application/json" },
    ...opts,
  });
  if (!r.ok) {
    let msg;
    try {
      const body = await r.json();
      msg =
        body?.detail?.message ||
        body?.detail?.conflict ||
        (typeof body?.detail === "string" ? body.detail : null) ||
        body?.message ||
        r.statusText;
    } catch {
      msg = r.statusText;
    }
    throw Object.assign(new Error(msg || r.statusText), { status: r.status });
  }
  return r.json();
}

const REGISTRATION_STATUSES = ["", "CONFIRMED", "WITHDRAWN", "PENDING"];

export default function DeliveryRegistrations() {
  const [registrations, setRegistrations] = useState([]);
  const [offerings, setOfferings]         = useState([]);
  const [loading, setLoading]             = useState(true);

  // Filters
  const [filterOffering, setFilterOffering] = useState("");
  const [filterStatus, setFilterStatus]     = useState("");

  // Withdraw modal
  const [withdrawing, setWithdrawing]   = useState(null); // registration_id
  const [withdrawReason, setWithdrawReason] = useState("");
  const [savingWithdraw, setSavingWithdraw] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterOffering) params.set("offering_id", filterOffering);
      if (filterStatus)   params.set("status", filterStatus);
      const [regs, offs] = await Promise.all([
        apiFetch(`/delivery/registrations?${params}`),
        apiFetch("/delivery/offerings"),
      ]);
      setRegistrations(regs);
      setOfferings(offs);
    } catch (e) {
      toast.error("Load failed: " + e.message);
    } finally {
      setLoading(false);
    }
  }, [filterOffering, filterStatus]);

  useEffect(() => { load(); }, [load]);

  const handleWithdraw = async (e) => {
    e.preventDefault();
    if (!withdrawReason.trim()) { toast.error("Withdrawal reason required"); return; }
    setSavingWithdraw(true);
    try {
      await apiFetch(`/delivery/registrations/${withdrawing}/withdraw`, {
        method: "POST",
        body: JSON.stringify({ withdrawal_reason: withdrawReason.trim() }),
      });
      toast.success("Registration withdrawn");
      setWithdrawing(null);
      setWithdrawReason("");
      load();
    } catch (e) {
      toast.error(`Withdraw failed (${e.status || ""}): ${e.message}`);
    } finally {
      setSavingWithdraw(false);
    }
  };

  const offeringMap = offerings.reduce((acc, o) => {
    acc[o.id] = o.title || o.code;
    return acc;
  }, {});

  return (
    <AssessmentDeliveryShell title="Registrations" breadcrumb="Registrations">
      {/* ── Toolbar ──────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <select
          value={filterOffering}
          onChange={e => setFilterOffering(e.target.value)}
          data-testid="filter-offering-select"
          className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-xs
                     text-slate-300 focus:outline-none focus:border-emerald-600"
        >
          <option value="">All Offerings</option>
          {offerings.map(o => (
            <option key={o.id} value={o.id}>{o.code} — {o.title || o.id}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          data-testid="filter-status-select"
          className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-xs
                     text-slate-300 focus:outline-none focus:border-emerald-600"
        >
          {REGISTRATION_STATUSES.map(s => (
            <option key={s} value={s}>{s || "All Statuses"}</option>
          ))}
        </select>
        <div className="flex-1" />
        <button
          type="button"
          onClick={load}
          disabled={loading}
          data-testid="registrations-refresh"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-slate-400
                     border border-slate-700 bg-slate-900/60 hover:bg-slate-800 transition-colors"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* ── Registrations list ───────────────────────────────────────────── */}
      {loading ? (
        <div className="text-sm text-slate-500 py-12 text-center" data-testid="registrations-loading">
          Loading registrations…
        </div>
      ) : registrations.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-700 p-12 text-center"
             data-testid="registrations-empty">
          <Users className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400 text-sm">No registrations found.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="registrations-list">
          {registrations.map(reg => (
            <div
              key={reg.id}
              className="flex items-center gap-4 rounded-xl border border-slate-800 bg-slate-900/50 px-5 py-4"
              data-testid={`reg-row-${reg.id}`}
            >
              <DeliveryStatusBadge status={reg.status} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-200 truncate">
                  {offeringMap[reg.offering_id] || reg.offering_id?.slice(0, 12) + "…"}
                </p>
                <p className="text-xs text-slate-500 font-mono mt-0.5 truncate">
                  Student: {reg.student_id}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-500">{reg.created_at?.slice(0, 10)}</p>
                <p className="text-xs text-slate-600 font-mono">{reg.id?.slice(0, 12)}…</p>
              </div>
              {reg.status === "CONFIRMED" && (
                <button
                  type="button"
                  onClick={() => setWithdrawing(reg.id)}
                  data-testid={`withdraw-btn-${reg.id}`}
                  className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs border
                             border-red-700/60 text-red-300 bg-red-950/30
                             hover:bg-red-900/40 transition-colors"
                >
                  <AlertTriangle className="w-3 h-3" /> Withdraw
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Withdraw modal ───────────────────────────────────────────────── */}
      {withdrawing && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
             data-testid="withdraw-modal">
          <div className="max-w-sm w-full rounded-2xl border border-slate-700 bg-slate-900 overflow-hidden shadow-2xl">
            <div className="px-6 py-6">
              <h2 className="text-base font-semibold text-slate-100 mb-2">Withdraw Registration</h2>
              <p className="text-xs text-slate-400 mb-4">
                This cannot be undone. The student will no longer be able to attempt this offering.
              </p>
              <form onSubmit={handleWithdraw}>
                <label className="block text-xs text-slate-400 mb-1">Reason *</label>
                <textarea
                  value={withdrawReason}
                  onChange={e => setWithdrawReason(e.target.value)}
                  rows={3}
                  required
                  data-testid="withdraw-reason-input"
                  placeholder="Reason for withdrawal…"
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm
                             text-slate-200 placeholder-slate-600 resize-none focus:outline-none
                             focus:border-red-600 mb-4"
                />
                <div className="flex gap-3">
                  <button
                    type="submit"
                    disabled={savingWithdraw}
                    data-testid="confirm-withdraw-btn"
                    className="flex-1 py-2 rounded-xl text-sm font-semibold bg-red-700
                               hover:bg-red-600 text-white transition-colors disabled:opacity-50"
                  >
                    {savingWithdraw ? "Withdrawing…" : "Confirm Withdraw"}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setWithdrawing(null); setWithdrawReason(""); }}
                    data-testid="cancel-withdraw-btn"
                    className="px-4 py-2 rounded-xl text-sm text-slate-400 border border-slate-700
                               hover:border-slate-600 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </AssessmentDeliveryShell>
  );
}
